import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm, Controller } from 'react-hook-form';

// Components
import Heading from '../../../../Heading';
import Subheading from '../../../../Subheading';
import SelectField from '../../../../SelectField';
import InputField from '../../../../InputField';
import TextareaField from '../../../../TextareaField';
import AppLink from '../../../../AppLink';
import ImageLoader from '../../../../ImageLoader';
import Button from '../../../../Button';
import Success from '../Success';

// Router
import { getRoute } from '../../../../../entry/routes';

// State
import {
  fetchFeedbackData,
  resetFeedback,
  sendFeedback,
} from '../../../../../state/modules/feedback/actions';
import { fetchOrderListIsNeeded } from '../../../../../state/modules/order/actions';

// Utils
import { required, isEmail } from '../../../../../utils/validators';

// Config
import config from '../../../../../config';

// Styles
import styles from './FullForm.styl';

const cx = classNames.bind(styles);

export default function FullForm(props) {
  const { className } = props;

  const intl = useIntl();
  const dispatch = useDispatch();
  const {
    sendingData: { isSending, isSent },
    settings: { cities = [], feedback_themes = [], restaurants = [], order_number_example = '' },
  } = useSelector(state => state.feedback);
  const {
    isAuthenticated,
    profile: { email = '' },
  } = useSelector(state => state.user);
  const id = useSelector(state => state.city.userCity.id);
  const lastOrder = useSelector(state => state.order.list[0]) || {};

  const [reCaptchaKey, setReCaptchaKey] = useState('');
  const [files, setFiles] = useState([]);
  const [isFirstStep, setIsFirstStep] = useState(true);

  const { register, handleSubmit, errors, control, setValue, trigger, getValues, reset } = useForm({
    mode: 'onBlur',
    defaultValues: {
      city_id: id,
      theme_id: null,
      text: '',
      restaurant_id: lastOrder.restaurant_id || null,
      order_number: lastOrder.client_number || '',
      email,
    },
  });

  const onFirstStepSubmit = async () => {
    const result = await trigger(['city_id', 'theme_id', 'text']);

    return result ? setIsFirstStep(false) : false;
  };

  const onSubmit = handleSubmit(formValues => {
    return dispatch(
      sendFeedback({ ...formValues, file: files, g_recaptcha_response: reCaptchaKey }),
    );
  });

  const saveFiles = newFiles => {
    setFiles([...files, ...newFiles]);
  };

  const removeFiles = imageIndex => {
    const newFiles = files.filter((image, key) => key !== imageIndex);

    setFiles(newFiles);
  };

  const showForm = () => {
    dispatch(resetFeedback());
    setIsFirstStep(true);
    reset();
    setFiles([]);
    setReCaptchaKey('');
  };

  const handleCityChange = value => {
    dispatch(fetchFeedbackData(value));
  };

  useEffect(() => {
    dispatch(fetchFeedbackData(id));

    return () => {
      dispatch(resetFeedback());
    };
  }, []);

  useEffect(() => {
    dispatch(fetchOrderListIsNeeded());
  }, [isAuthenticated]);

  useEffect(() => {
    if (feedback_themes.length > 0) {
      setValue('theme_id', feedback_themes[0].id, { shouldValidate: true });
    }
  }, [feedback_themes]);

  useEffect(() => {
    const currentRestaurantId = getValues('restaurant_id');

    if (restaurants.length > 0 && !currentRestaurantId) {
      setValue('restaurant_id', restaurants[0].id, { shouldValidate: true });
    }
  }, [restaurants]);

  useEffect(() => {
    if (lastOrder.id) {
      setValue('restaurant_id', lastOrder.restaurant_id, { shouldValidate: true });
      setValue('order_number', lastOrder.client_number, { shouldValidate: true });
    }
  }, [lastOrder]);

  useEffect(() => {
    if (email) {
      setValue('email', email, { shouldValidate: true });
    }
  }, [email]);

  if (isSent) {
    return <Success onClick={showForm} />;
  }

  return (
    <form className={cx('FeedbackForm', className)} onSubmit={onSubmit}>
      <Heading className={cx('FeedbackForm__title')} level={2}>
        {intl.formatMessage({ id: 'feedback.title' })}
      </Heading>
      <div className={cx('FeedbackForm__step', { FeedbackForm__step_visible: isFirstStep })}>
        <div className={cx('FeedbackForm__content')}>
          <div className={cx('FeedbackForm__column')}>
            <div className={cx('FeedbackForm__row')}>
              <Controller
                name="city_id"
                control={control}
                rules={{ validate: { required } }}
                render={({ onChange, value, name }) => (
                  <SelectField
                    options={cities}
                    name={name}
                    value={value}
                    labelKey="name"
                    valueKey="id"
                    onChange={onChange}
                    onSelect={handleCityChange}
                    label={intl.formatMessage({ id: 'field.selectCity.label' })}
                    placeholder={intl.formatMessage({ id: 'field.select.placeholder' })}
                  />
                )}
              />
              {errors.city_id && (
                <div className={cx('FeedbackForm__error')}>{errors.city_id.message}</div>
              )}
            </div>
            <div className={cx('FeedbackForm__row')}>
              <Controller
                name="theme_id"
                control={control}
                rules={{ validate: { required } }}
                render={({ onChange, value, name }) => (
                  <SelectField
                    options={feedback_themes}
                    name={name}
                    value={value}
                    labelKey="theme"
                    valueKey="id"
                    onChange={onChange}
                    label={intl.formatMessage({ id: 'field.theme.label' })}
                    placeholder={intl.formatMessage({ id: 'field.select.placeholder' })}
                  />
                )}
              />
              {errors.theme_id && (
                <div className={cx('FeedbackForm__error')}>{errors.theme_id.message}</div>
              )}
            </div>
          </div>
          <div className={cx('FeedbackForm__column')}>
            <div className={cx('FeedbackForm__row')}>
              <TextareaField
                name="text"
                register={register({ validate: { required } })}
                label={intl.formatMessage({ id: 'field.feedbackMessage.placeholder' })}
                placeholder={intl.formatMessage({ id: 'field.feedbackMessage.placeholder' })}
              />
              {errors.text && (
                <div className={cx('FeedbackForm__error')}>{errors.text.message}</div>
              )}
            </div>
          </div>
        </div>
        <div className={cx('FeedbackForm__footer')}>
          <Subheading className={cx('FeedbackForm__privacy')}>
            <FormattedMessage
              id="feedback.agree"
              values={{
                link: (
                  <AppLink to={getRoute('personal-data-processing-policy')}>
                    <FormattedMessage id="feedback.agree.link" />
                  </AppLink>
                ),
              }}
            />
          </Subheading>
          <Button onClick={onFirstStepSubmit}>
            <FormattedMessage id="button.continue" />
          </Button>
        </div>
      </div>
      <div className={cx('FeedbackForm__step', { FeedbackForm__step_visible: !isFirstStep })}>
        <div className={cx('FeedbackForm__content')}>
          <div className={cx('FeedbackForm__column')}>
            <div className={cx('FeedbackForm__row')}>
              <Controller
                name="restaurant_id"
                control={control}
                rules={{ validate: { required } }}
                render={({ onChange, value, name }) => (
                  <SelectField
                    options={restaurants}
                    name={name}
                    value={value}
                    labelKey="title"
                    valueKey="id"
                    onChange={onChange}
                    label={intl.formatMessage({ id: 'field.selectRestaurant.label' })}
                    placeholder={intl.formatMessage({ id: 'field.select.placeholder' })}
                  />
                )}
              />
              {errors.restaurant_id && (
                <div className={cx('FeedbackForm__error')}>{errors.restaurant_id.message}</div>
              )}
            </div>
            <div className={cx('FeedbackForm__row')}>
              <FormattedMessage
                id="field.orderNumber.placeholder"
                values={{ text: order_number_example }}
              >
                {placeholder => (
                  <InputField
                    name="order_number"
                    register={register({ validate: { required } })}
                    label={intl.formatMessage({ id: 'field.orderNumber.label' })}
                    placeholder={order_number_example && placeholder}
                  />
                )}
              </FormattedMessage>
              {errors.order_number && (
                <div className={cx('FeedbackForm__error')}>{errors.order_number.message}</div>
              )}
            </div>
          </div>
          <div className={cx('FeedbackForm__column')}>
            <div className={cx('FeedbackForm__row')}>
              <InputField
                name="email"
                register={register({ validate: { required, isEmail } })}
                label={intl.formatMessage({ id: 'field.email.label' })}
              />
              {errors.email && (
                <div className={cx('FeedbackForm__error')}>{errors.email.message}</div>
              )}
            </div>
            <ReCAPTCHA
              sitekey={config.reCaptchaSiteKey}
              onChange={value => setReCaptchaKey(value)}
            />
          </div>
        </div>
        <div className={cx('FeedbackForm__footer')}>
          <ImageLoader
            className={cx('FeedbackForm__image-loader')}
            allowedExtensions="image/jpeg, image/gif, image/png"
            maxFileSize={32 * 1024 * 1024}
            maxFilesAmount={5}
            onLoadFiles={saveFiles}
            onRemoveFile={removeFiles}
          />
          <div className={cx('FeedbackForm__actions')}>
            <Button className={cx('FeedbackForm__back')} onClick={() => setIsFirstStep(true)} link>
              <FormattedMessage id="button.back" />
            </Button>
            <Button type="submit" isLoading={isSending} disabled={!reCaptchaKey || isFirstStep}>
              <FormattedMessage id="button.submit" />
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}

FullForm.defaultProps = {
  className: '',
};

FullForm.propTypes = {
  className: PropTypes.string,
};
